import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FormContainer from '../../components/ui/FormContainer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { generateId } from '../../utils/generateId';
import { loadFormData, saveProgress, getUserEmail, getLocationId, useCoalescedSave, AUTOSAVE_ON_CHANGE } from '../../utils/onboardingApi';

const BusinessInformationForm = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    companyLegalName: '',
    companyBrandName: '',
    companyWebsite: '',
    linkedinUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    streetAddress: '',
    city: '',
    stateProvince: '',
    zipPostalCode: '',
    einDocument: null,
    importFromPreviousDeal: ''
  });

  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedDealForImport, setSelectedDealForImport] = useState('');
  const [dealOptions, setDealOptions] = useState([]);
  const [currentBusinessInfoId, setCurrentBusinessInfoId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [driveUploadComplete, setDriveUploadComplete] = useState(false);


  // Load saved form data from API on component mount
  useEffect(() => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.warn('No email found. User may need to access via email link.');
      setIsLoading(false);
      return;
    }

    // Try to load from API
    loadFormData('business-info')
      .then(data => {
        if (Object.keys(data).length > 0) {
          setFormData(prev => ({
            ...prev,
            companyLegalName: data?.companyLegalName || prev?.companyLegalName,
            companyBrandName: data?.companyBrandName || prev?.companyBrandName,
            companyWebsite: data?.companyWebsite || prev?.companyWebsite,
            linkedinUrl: data?.linkedinUrl || prev?.linkedinUrl,
            twitterUrl: data?.twitterUrl || prev?.twitterUrl,
            facebookUrl: data?.facebookUrl || prev?.facebookUrl,
            instagramUrl: data?.instagramUrl || prev?.instagramUrl,
            streetAddress: data?.streetAddress || prev?.streetAddress,
            city: data?.city || prev?.city,
            stateProvince: data?.stateProvince || prev?.stateProvince,
            zipPostalCode: data?.zipPostalCode || prev?.zipPostalCode,
            einDocument: data?.einDocument || null
          }));
          // Check if loaded data has confirmed Drive upload (fileId present)
          if (data?.einDocument?.fileId) {
            setDriveUploadComplete(true);
          }
        } else {
          // Fallback to localStorage for legacy data
          const savedData = localStorage.getItem('businessInformationDraft');
          if (savedData) {
            try {
              const parsed = JSON.parse(savedData);
              setFormData(prev => ({...prev, ...parsed}));
            } catch (e) {
              console.error('Failed to parse localStorage data:', e);
            }
          }
        }
      })
      .catch(err => {
        console.error('Error loading form data:', err);
        // Fallback to localStorage on API error
        const savedData = localStorage.getItem('businessInformationDraft');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFormData(prev => ({...prev, ...parsed}));
          } catch (e) {
            console.error('Failed to parse localStorage data:', e);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    // Load active deals for import dropdown
    try {
      const activeDeals = JSON.parse(localStorage.getItem('activeDeals') || '[]');
      const options = activeDeals.map(deal => ({
        value: String(deal.id),
        label: deal.dealName || 'Unnamed Deal'
      }));
      setDealOptions(options);
    } catch (error) {
      console.error('Failed to load active deals:', error);
    }
  }, []);

  // Build the save payload from current formData. Shared by both the legacy
  // autosave useEffect below AND the new useCoalescedSave hook so the
  // serialization logic only lives in one place.
  const buildPayload = () => ({
    companyLegalName: formData?.companyLegalName,
    companyBrandName: formData?.companyBrandName,
    companyWebsite: formData?.companyWebsite,
    linkedinUrl: formData?.linkedinUrl,
    twitterUrl: formData?.twitterUrl,
    facebookUrl: formData?.facebookUrl,
    instagramUrl: formData?.instagramUrl,
    streetAddress: formData?.streetAddress,
    city: formData?.city,
    stateProvince: formData?.stateProvince,
    zipPostalCode: formData?.zipPostalCode,
    einDocument: formData?.einDocument ? (() => {
      const base = {
        name: formData?.einDocument?.name,
        size: formData?.einDocument?.size,
        type: formData?.einDocument?.type
      };
      if (formData?.einDocument?.fileId) base.fileId = formData.einDocument.fileId;
      if (formData?.einDocument?.url) base.url = formData.einDocument.url;
      if (formData?.einDocument?.filename) base.filename = formData.einDocument.filename;
      return base;
    })() : null
  });

  // NEW: coalesced-save behavior (blur + navigation + tab close).
  // To disable and fall back to the legacy autosave-on-change useEffect
  // below, flip AUTOSAVE_ON_CHANGE to `true` in src/utils/onboardingApi.js.
  const { handleBlur } = useCoalescedSave({
    step: 'business-info',
    buildPayload,
    isLoading
  });

  // LEGACY: autosave on every formData change (debounced 5s).
  // Gated by AUTOSAVE_ON_CHANGE — disabled by default. Kept in place so a
  // single flag flip in onboardingApi.js restores the old behavior without
  // any code change here.
  useEffect(() => {
    if (!AUTOSAVE_ON_CHANGE) return;
    if (isLoading) return; // Don't save during initial load

    const timeoutId = setTimeout(() => {
      saveProgress('business-info', buildPayload())
        .then(() => {})
        .catch(err => {
          console.error('Failed to auto-save:', err);
          // Fallback to localStorage on API error
          localStorage.setItem('businessInformationDraft_backup', JSON.stringify(formData));
        });
    }, 5000); // Debounce for 5 seconds

    return () => clearTimeout(timeoutId);
  }, [formData, isLoading]);

  // Define required fields (all except social media)
  const requiredFields = [
    'companyLegalName',
    'companyWebsite',
    'streetAddress',
    'city',
    'stateProvince',
    'zipPostalCode',
    'einDocument'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    requiredFields?.forEach(field => {
      if (field === 'einDocument') {
        if (!formData?.[field]) {
          newErrors[field] = 'EIN document is required';
        }
      } else {
        if (!formData?.[field]?.trim()) {
          newErrors[field] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleBack = () => {
    navigate('/brand-guidelines-setup');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset drive upload state when EIN file changes
    if (field === 'einDocument') {
      setDriveUploadComplete(false);
      setUploadError('');
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      const file = e?.dataTransfer?.files?.[0];
      if (file?.size <= 10 * 1024 * 1024) { // 10MB limit
        handleInputChange('einDocument', file);
      } else {
        alert('File size must be less than 10MB');
      }
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      const file = e?.target?.files?.[0];
      if (file?.size <= 10 * 1024 * 1024) { // 10MB limit
        handleInputChange('einDocument', file);
      } else {
        alert('File size must be less than 10MB');
      }
    }
  };

  const handleImportFromDeal = () => {
    if (!selectedDealForImport) return;
    
    try {
      const activeDeals = JSON.parse(localStorage.getItem('activeDeals') || '[]');
      const selectedDeal = activeDeals.find(deal => String(deal.id) === String(selectedDealForImport));
      
      if (selectedDeal && selectedDeal.fullData) {
        const importData = selectedDeal.fullData;
        
        // Import business information fields
        setFormData(prev => ({
          ...prev,
          companyLegalName: importData.companyLegalName || prev.companyLegalName,
          companyBrandName: importData.companyBrandName || prev.companyBrandName,
          companyWebsite: importData.companyWebsite || prev.companyWebsite,
          linkedinUrl: importData.linkedinUrl || prev.linkedinUrl,
          twitterUrl: importData.twitterUrl || prev.twitterUrl,
          facebookUrl: importData.facebookUrl || prev.facebookUrl,
          instagramUrl: importData.instagramUrl || prev.instagramUrl,
          streetAddress: importData.streetAddress || prev.streetAddress,
          city: importData.city || prev.city,
          stateProvince: importData.stateProvince || prev.stateProvince,
          zipPostalCode: importData.zipPostalCode || prev.zipPostalCode,
          einDocument: importData.einDocument || prev.einDocument
        }));
      }
    } catch (error) {
      console.error('Failed to import from deal:', error);
    }
  };

  const handleSaveExit = async () => {
    try {
      const dataToSave = {
        companyLegalName: formData?.companyLegalName,
        companyBrandName: formData?.companyBrandName,
        companyWebsite: formData?.companyWebsite,
        linkedinUrl: formData?.linkedinUrl,
        twitterUrl: formData?.twitterUrl,
        facebookUrl: formData?.facebookUrl,
        instagramUrl: formData?.instagramUrl,
        streetAddress: formData?.streetAddress,
        city: formData?.city,
        stateProvince: formData?.stateProvince,
        zipPostalCode: formData?.zipPostalCode,
        einDocument: serializeEinDocument(formData?.einDocument),
        createdAt: new Date().toISOString()
      };

      await saveProgress('business-info', dataToSave);
      navigate('/deals-dashboard-empty-state');
    } catch (error) {
      console.error('Failed to save form data:', error);
      alert('Failed to save form data. Please check your connection and try again.');
    }
  };

  const serializeEinDocument = (doc) => {
    if (!doc) return null;
    const base = {
      name: doc?.name,
      size: doc?.size,
      type: doc?.type
    };
    if (doc?.fileId) base.fileId = doc.fileId;
    if (doc?.url) base.url = doc.url;
    if (doc?.filename) base.filename = doc.filename;
    return base;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setUploadError('');

    // If EIN was already successfully uploaded to Drive, skip re-upload
    if (driveUploadComplete) {
      const isNewFile = formData?.einDocument instanceof File;
      if (!isNewFile) {
        try {
          const dataToSave = {
            companyLegalName: formData?.companyLegalName,
            companyBrandName: formData?.companyBrandName,
            companyWebsite: formData?.companyWebsite,
            linkedinUrl: formData?.linkedinUrl,
            twitterUrl: formData?.twitterUrl,
            facebookUrl: formData?.facebookUrl,
            instagramUrl: formData?.instagramUrl,
            streetAddress: formData?.streetAddress,
            city: formData?.city,
            stateProvince: formData?.stateProvince,
            zipPostalCode: formData?.zipPostalCode,
            einDocument: serializeEinDocument(formData?.einDocument),
            createdAt: new Date().toISOString()
          };
          await saveProgress('business-info', dataToSave);
          navigate('/investor-relations-contact');
          return;
        } catch (error) {
          console.error('Failed to save form data:', error);
          alert('Failed to save form data. Please check your connection and try again.');
          return;
        }
      }
    }

    // Check for stale metadata - EIN exists but is not a File and has no fileId
    if (formData?.einDocument && !(formData.einDocument instanceof File) && !formData.einDocument.fileId) {
      setUploadError('EIN document needs to be re-selected. Please remove and re-upload it.');
      return;
    }

    // Upload EIN to Drive if it's a new File
    if (formData?.einDocument && formData.einDocument instanceof File) {
      setIsUploading(true);
      setUploadProgress('Uploading EIN document...');
      try {
        const uploadFormData = new FormData();
        const locationId = getLocationId();

        if (!locationId) {
          throw new Error('Location ID is required for file upload');
        }

        uploadFormData.append('locationId', locationId);
        uploadFormData.append('folderPath', 'Inbox');
        uploadFormData.append('files', formData.einDocument);

        const response = await fetch(
          'https://raiseai-api-production.up.railway.app/api/media/upload-to-drive',
          {
            method: 'POST',
            body: uploadFormData
          }
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();
        const uploadResult = result.results?.[0];

        if (uploadResult?.success) {
          const confirmedEin = {
            name: formData.einDocument.name,
            size: formData.einDocument.size,
            type: formData.einDocument.type,
            url: uploadResult.url,
            fileId: uploadResult.fileId,
            filename: uploadResult.filename
          };

          setFormData(prev => ({ ...prev, einDocument: confirmedEin }));
          setDriveUploadComplete(true);
          setUploadProgress('Upload complete!');

          // Save confirmed data with fileId to API
          const dataToSave = {
            companyLegalName: formData?.companyLegalName,
            companyBrandName: formData?.companyBrandName,
            companyWebsite: formData?.companyWebsite,
            linkedinUrl: formData?.linkedinUrl,
            twitterUrl: formData?.twitterUrl,
            facebookUrl: formData?.facebookUrl,
            instagramUrl: formData?.instagramUrl,
            streetAddress: formData?.streetAddress,
            city: formData?.city,
            stateProvince: formData?.stateProvince,
            zipPostalCode: formData?.zipPostalCode,
            einDocument: confirmedEin,
            createdAt: new Date().toISOString()
          };

          await saveProgress('business-info', dataToSave);
          navigate('/investor-relations-contact');
        } else {
          throw new Error('Upload result did not indicate success');
        }
      } catch (error) {
        console.error('Failed to upload EIN document:', error);
        setUploadError('Failed to upload EIN document. Please try again.');
        setUploadProgress('');
      } finally {
        setIsUploading(false);
      }
    } else {
      // No file to upload (einDocument is already confirmed or null)
      try {
        const dataToSave = {
          companyLegalName: formData?.companyLegalName,
          companyBrandName: formData?.companyBrandName,
          companyWebsite: formData?.companyWebsite,
          linkedinUrl: formData?.linkedinUrl,
          twitterUrl: formData?.twitterUrl,
          facebookUrl: formData?.facebookUrl,
          instagramUrl: formData?.instagramUrl,
          streetAddress: formData?.streetAddress,
          city: formData?.city,
          stateProvince: formData?.stateProvince,
          zipPostalCode: formData?.zipPostalCode,
          einDocument: serializeEinDocument(formData?.einDocument),
          createdAt: new Date().toISOString()
        };

        await saveProgress('business-info', dataToSave);
        navigate('/investor-relations-contact');
      } catch (error) {
        console.error('Failed to save form data:', error);
        alert('Failed to save form data. Please check your connection and try again.');
      }
    }
  };

  const isRequired = (field) => requiredFields?.includes(field);

  return (
    <FormContainer maxWidth="max-w-2xl">
      {/* onBlur on wrapper catches blur events bubbling from any child input.
          This is what drives the new coalesced save on field exit. */}
      <div className="space-y-6" onBlur={handleBlur}>
        {/* Header */}
        <div className="space-y-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Business Information</h1>
            <p className="text-gray-600">Provide your company details for verification</p>
            <p className="text-sm text-red-600 mt-2">* Required fields</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Import from Previous Deal */}
          {dealOptions.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Import from Previous Deal</h3>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Select
                    options={[{ value: '', label: 'Select a deal...' }, ...dealOptions]}
                    value={selectedDealForImport}
                    onChange={setSelectedDealForImport}
                    placeholder="Select a deal..."
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleImportFromDeal}
                  disabled={!selectedDealForImport}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Import
                </Button>
              </div>
            </div>
          )}

          {/* Company Details Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Company Details</h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Legal Name {isRequired('companyLegalName') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.companyLegalName}
                onChange={(e) => handleInputChange('companyLegalName', e?.target?.value)}
                placeholder="Enter official registered name"
                className={`w-full ${errors?.companyLegalName ? 'border-red-500' : ''}`}
                required={isRequired('companyLegalName')}
              />
              {errors?.companyLegalName && (
                <p className="text-sm text-red-600">{errors?.companyLegalName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Brand Name (DBA) {isRequired('companyBrandName') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.companyBrandName}
                onChange={(e) => handleInputChange('companyBrandName', e?.target?.value)}
                placeholder="Enter doing business as name if different"
                className={`w-full ${errors?.companyBrandName ? 'border-red-500' : ''}`}
                required={isRequired('companyBrandName')}
              />
              {errors?.companyBrandName && (
                <p className="text-sm text-red-600">{errors?.companyBrandName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Company Website {isRequired('companyWebsite') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="url"
                value={formData?.companyWebsite}
                onChange={(e) => handleInputChange('companyWebsite', e?.target?.value)}
                placeholder="https://www.example.com"
                className={`w-full ${errors?.companyWebsite ? 'border-red-500' : ''}`}
                required={isRequired('companyWebsite')}
              />
              {errors?.companyWebsite && (
                <p className="text-sm text-red-600">{errors?.companyWebsite}</p>
              )}
            </div>
          </div>

          {/* Social Media Presence Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Social Media Presence <span className="text-sm text-gray-500 font-normal">(Optional)</span></h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">LinkedIn URL</label>
              <Input
                type="url"
                value={formData?.linkedinUrl}
                onChange={(e) => handleInputChange('linkedinUrl', e?.target?.value)}
                placeholder="https://linkedin.com/company/..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Twitter/X URL</label>
              <Input
                type="url"
                value={formData?.twitterUrl}
                onChange={(e) => handleInputChange('twitterUrl', e?.target?.value)}
                placeholder="https://twitter.com/..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
              <Input
                type="url"
                value={formData?.facebookUrl}
                onChange={(e) => handleInputChange('facebookUrl', e?.target?.value)}
                placeholder="https://facebook.com/..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Instagram URL</label>
              <Input
                type="url"
                value={formData?.instagramUrl}
                onChange={(e) => handleInputChange('instagramUrl', e?.target?.value)}
                placeholder="https://instagram.com/..."
                className="w-full"
              />
            </div>
          </div>

          {/* Business Address Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Business Address</h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Street Address {isRequired('streetAddress') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.streetAddress}
                onChange={(e) => handleInputChange('streetAddress', e?.target?.value)}
                placeholder="Enter street address"
                className={`w-full ${errors?.streetAddress ? 'border-red-500' : ''}`}
                required={isRequired('streetAddress')}
              />
              {errors?.streetAddress && (
                <p className="text-sm text-red-600">{errors?.streetAddress}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                City {isRequired('city') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                placeholder="Enter city"
                className={`w-full ${errors?.city ? 'border-red-500' : ''}`}
                required={isRequired('city')}
              />
              {errors?.city && (
                <p className="text-sm text-red-600">{errors?.city}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                State/Province {isRequired('stateProvince') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.stateProvince}
                onChange={(e) => handleInputChange('stateProvince', e?.target?.value)}
                placeholder="Enter state or province"
                className={`w-full ${errors?.stateProvince ? 'border-red-500' : ''}`}
                required={isRequired('stateProvince')}
              />
              {errors?.stateProvince && (
                <p className="text-sm text-red-600">{errors?.stateProvince}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                ZIP/Postal Code {isRequired('zipPostalCode') && <span className="text-red-600">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.zipPostalCode}
                onChange={(e) => handleInputChange('zipPostalCode', e?.target?.value)}
                placeholder="Enter ZIP or postal code"
                className={`w-full ${errors?.zipPostalCode ? 'border-red-500' : ''}`}
                required={isRequired('zipPostalCode')}
              />
              {errors?.zipPostalCode && (
                <p className="text-sm text-red-600">{errors?.zipPostalCode}</p>
              )}
            </div>


          </div>

          {/* Tax Information Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Tax Information</h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Proof of EIN (Employer Identification Number) {isRequired('einDocument') && <span className="text-red-600">*</span>}
              </label>
              <p className="text-sm text-gray-600">Required for any tax messaging capability</p>
              
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-purple-400 bg-purple-50' 
                    : errors?.einDocument 
                      ? 'border-red-400 bg-red-50' :'border-gray-300 bg-gray-50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                <div className="space-y-2">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  
                  {formData?.einDocument ? (
                    <div>
                      <p className="text-sm font-medium text-green-600">File uploaded: {formData?.einDocument?.name}</p>
                      <p className="text-xs text-gray-500">Click or drag to replace</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600">Drag and drop your EIN document here</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (max 10MB)</p>
                    </div>
                  )}
                </div>
                
                <Button
                  type="button"
                  className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => document.querySelector('input[type="file"]')?.click()}
                >
                  Browse Files
                </Button>
              </div>
              {errors?.einDocument && (
                <p className="text-sm text-red-600">{errors?.einDocument}</p>
              )}
            </div>
          </div>

          {/* Upload Error */}
          {uploadError && (
            <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              <span>{uploadError}</span>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && uploadProgress && (
            <div className="flex items-center space-x-3 text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span>{uploadProgress}</span>
            </div>
          )}

          {/* Bottom Navigation */}
          <div className="pt-6">
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </FormContainer>
  );
};

export default BusinessInformationForm;